import { difference } from 'es-toolkit/compat';
import { Wildfire } from 'features/wildfires/wildfire.types';
import { RelatedWildfire } from 'features/wildfires/wildfireGeoEventData.types';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type UseComplexWildfireFormFieldChangesProps = {
  wildfire: Wildfire;
};

type FormValues = {
  isComplexParent: boolean;
  childWildfires: RelatedWildfire[];
};

type ChangeKey = keyof FormValues;

type ChangeMap = Partial<Record<ChangeKey, string>>;

export const useComplexWildfireFormFieldChanges = (
  props: UseComplexWildfireFormFieldChangesProps,
): ChangeMap => {
  const { wildfire } = props;
  const { t } = useTranslation();

  const [isComplexParent, childWildfires] = useWatch<FormValues>({
    name: ['isComplexParent', 'childWildfires'],
  }) as [boolean, RelatedWildfire[]];

  const isComplexParentChanged =
    wildfire.data.isComplexParent !== isComplexParent;
  const initialChildWildfires = wildfire.childGeoEvents.filter(
    (cge) => cge.geoEventType === 'wildfire',
  );

  const addedChildWildfires = difference(
    childWildfires.map((cw) => cw.id),
    initialChildWildfires.map((cw) => cw.id),
  );

  const removedChildWildfires = difference(
    initialChildWildfires.map((cw) => cw.id),
    childWildfires.map((cw) => cw.id),
  );

  const childWildfiresChanged =
    addedChildWildfires.length > 0 || removedChildWildfires.length > 0;

  const changes: ChangeMap = {};
  if (isComplexParentChanged) {
    changes.isComplexParent = t('geoEventChangelog.complexIncident');
  }
  if (childWildfiresChanged) {
    changes.childWildfires = t('geoEventChangelog.childWildfires');
  }

  return changes;
};
